import { useEffect, useRef, type RefObject } from 'react';

interface UseModalA11yOptions {
  /** Called on Escape. Read through a ref, so a new closure each render is fine. */
  onClose: () => void;
  /** Set false while the dialog is not shown (the hook is called before an early return). Default true. */
  open?: boolean;
  /** Element to focus on open instead of the panel itself (e.g. the first field). */
  initialFocusRef?: RefObject<HTMLElement | null>;
  /** Return focus to the element that opened the dialog on close. Default true. Read at close time. */
  returnFocus?: boolean;
  /** Where focus goes on close when the opener is gone (a menu item that unmounted) or there was none (opened by a shortcut, an event or a route). */
  fallbackFocusRef?: RefObject<HTMLElement | null>;
}

interface OpenDialog {
  id: symbol;
  panel: () => HTMLElement | null;
}

// Open dialogs, in the order their effects ran. React runs a CHILD's passive effects before its parent's, so order alone
// cannot say which dialog is on top: topmostDialog() also looks at the DOM.
const openDialogs: OpenDialog[] = [];

/**
 * The dialog that owns the keyboard: among open dialogs, one whose panel does not contain another open dialog's panel
 * (a nested child beats its parent however they registered), and of those the most recently registered.
 */
function topmostDialog(): OpenDialog | undefined {
  const panels = openDialogs.map((d) => d.panel());
  const candidates = openDialogs.filter((_, i) => {
    const panel = panels[i];
    return !panel || !panels.some((other, j) => j !== i && other && panel !== other && panel.contains(other));
  });
  return candidates[candidates.length - 1];
}

/** A key event an IME is still using (Safari reports the key that ends composition as keyCode 229, isComposing false). */
function isComposing(event: KeyboardEvent): boolean {
  return event.isComposing || event.keyCode === 229;
}

/**
 * Keyboard + focus behaviour for a hand-built modal overlay (WCAG 2.1.1, 2.4.3, 4.1.2).
 *
 *   const { ref, props } = useModalA11y({ onClose });
 *   <div ref={ref} {...props} aria-label="Report preview"> ... </div>
 *
 * `props` is role="dialog", aria-modal="true" and tabIndex -1 (give the dialog a name with aria-label or
 * aria-labelledby). While open, for the TOPMOST open dialog only:
 *  - Escape calls `onClose`, unless something else already handled the key (`event.defaultPrevented`, which a Radix
 *    popover or select inside the dialog sets), an IME is composing, or it is the auto-repeat of a held key (so one held
 *    Escape cannot close a nested child and then its parent);
 *  - focus moves to `initialFocusRef`, else into the panel, unless something inside already has it;
 *  - on close focus returns to the opener (`returnFocus`, read when the dialog closes). The opener is the element that
 *    had focus when the hook first rendered open, before any `autoFocus` field inside took it; with no opener left,
 *    focus goes to the open dialog beneath, if any.
 * Tab is left to the browser: no focus trap (WCAG 2.0 AA needs a meaningful order, 2.4.3, not containment).
 * A parent re-render, or a change to `returnFocus`, does not re-run the effect, so it never pulls focus away from a field
 * the user is typing in.
 */
export function useModalA11y({ onClose, open = true, initialFocusRef, returnFocus = true, fallbackFocusRef }: UseModalA11yOptions) {
  const ref = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const initialFocusRefRef = useRef(initialFocusRef);
  initialFocusRefRef.current = initialFocusRef;
  const fallbackFocusRefRef = useRef(fallbackFocusRef);
  fallbackFocusRefRef.current = fallbackFocusRef;
  const returnFocusRef = useRef(returnFocus);
  returnFocusRef.current = returnFocus;
  // Captured while rendering the open state: by the time any effect runs, React has already focused an autoFocus field
  // inside the panel, and that field would be "returned" to after it is gone.
  const openerRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);
  if (open !== wasOpenRef.current) {
    wasOpenRef.current = open;
    if (open && typeof document !== 'undefined') openerRef.current = document.activeElement as HTMLElement | null;
  }

  useEffect(() => {
    if (!open) return;
    const entry: OpenDialog = { id: Symbol('modal'), panel: () => ref.current };
    openDialogs.push(entry);
    const opener = openerRef.current;
    const panel = ref.current;
    const initial = initialFocusRefRef.current?.current;
    // Opening focus belongs to the topmost dialog only: a nested child's effect runs first, and its parent must not then
    // pull focus back out to a control that sits outside the child.
    if (topmostDialog() === entry) {
      if (initial) initial.focus();
      else if (panel && !panel.contains(document.activeElement)) panel.focus();
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented || event.repeat || isComposing(event)) return;
      if (topmostDialog() !== entry) return;
      event.preventDefault(); // tell any outer layer (another dialog, a page shortcut) that this Escape is taken
      onCloseRef.current();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      const at = openDialogs.indexOf(entry);
      if (at !== -1) openDialogs.splice(at, 1);
      if (!returnFocusRef.current) return;
      if (opener && opener !== document.body && typeof opener.focus === 'function' && document.contains(opener)) {
        opener.focus();
        return;
      }
      // No opener to go back to (nothing had focus, or it is gone): keep focus in the dialog still open beneath this one.
      const beneath = topmostDialog()?.panel();
      if (beneath && document.contains(beneath) && !beneath.contains(document.activeElement)) {
        beneath.focus();
        return;
      }
      const fallback = fallbackFocusRefRef.current?.current;
      if (!beneath && fallback && document.contains(fallback)) fallback.focus();
    };
  }, [open]);

  return { ref, props: { role: 'dialog' as const, 'aria-modal': true as const, tabIndex: -1 } };
}
